import { useState, useEffect, useMemo, memo } from "react";
import axios from "axios";
import { useGlobalState } from "../GlobalContext";
import RetrieveProductSettings from "../ProductSettings/RetrieveProductSettings";
import ProductSettings from "../ProductSettings/ProductSettings";
import { getSettingsId } from "../ProductSettings/Helper";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark, faPlus, faTrash, faCartPlus, faFileLines } from "@fortawesome/pro-regular-svg-icons";
import fetchLibraryProductPrice from "./fetchLibraryProductPrice";
import ModelViewer from "../ModelViewer/ModelViewer";
import { addProductToCart, requestSingleProductQuotation } from "./libraryCartUtils.jsx";

const PRESET_COLORS = ["#022955", "#360255", "#00924d", "#e67e22", "#e74c3c", "#3498db", "#9b59b6", "#1abc9c"];

// Isolated viewer that never re-renders after mount
const DetailModelViewer = memo(
    function DetailModelViewer({ modelUrl, extension, containerId, dimensions }) {
        return (
            <div id={containerId}>
                <div className="modelviewer-stats"></div>
                <ModelViewer
                    model={modelUrl}
                    extension={extension}
                    container={containerId}
                    width={250}
                    newModel={true}
                    preserveDrawingBuffer={true}
                    hideGrid={true}
                    dimensions={dimensions}
                />
            </div>
        );
    },
    () => true
);

export default function ProductDetail({
    product,
    onClose,
    onProductUpdated,
    onProductDeleted,
    onLabelCreated,
    textData,
    labels = [],
    navigateToCheckout,
    navigateToQuotations,
}) {
    const { productSettings, setProductSettings, setMandatoryProductSettings, loggedInUser, labelLogo } =
        useGlobalState();

    // Form state — initialized from product
    const [productName, setProductName] = useState(product.name);
    const [defaultQuantity, setDefaultQuantity] = useState(product.default_quantity || 1);
    const [selectedLabelIds, setSelectedLabelIds] = useState(product.labels ? product.labels.map((l) => l.id) : []);

    // Settings state
    const [chosenConfig, setChosenConfig] = useState({
        application: null,
        material: null,
        color: null,
        polish: null,
        coating: null,
        orientation: null,
        sterile: null,
        intendedUse: null,
        clinicalUse: null,
        custom: null,
    });

    // Inline label creation
    const [showNewLabel, setShowNewLabel] = useState(false);
    const [newLabelName, setNewLabelName] = useState("");
    const [newLabelColor, setNewLabelColor] = useState(PRESET_COLORS[0]);
    const [savingLabel, setSavingLabel] = useState(false);

    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(false);

    const isCustom = product.settings?.custom === "Yes";
    const hasFixedPrice = product.has_fixed_price === true;
    const settingsLocked = isCustom || hasFixedPrice;

    // Load product settings if not in context
    useEffect(() => {
        if (!productSettings || productSettings.length === 0) {
            RetrieveProductSettings({ setProductSettings, setMandatoryProductSettings });
        }
    }, []);

    // Resolve settingsId from chosen config
    const settingsId = useMemo(() => {
        if (isCustom) return product.settings_id;
        if (!productSettings || productSettings.length === 0) return "";
        return getSettingsId(chosenConfig, productSettings);
    }, [chosenConfig, productSettings, isCustom]);

    const canSave = productName.trim() && (isCustom || settingsId) && !saving;

    const labelId = window.localStorage.getItem("label_id");
    const baseUrl = window.location.origin + `/api/${labelId}/library`;

    // Create label inline
    const handleCreateLabel = async () => {
        if (!newLabelName.trim() || savingLabel) return;
        setSavingLabel(true);
        try {
            const response = await axios.post(`${baseUrl}/labels`, {
                name: newLabelName.trim(),
                color: newLabelColor,
            });
            const created = response.data;
            onLabelCreated(created);
            setSelectedLabelIds((prev) => [...prev, created.id]);
            setShowNewLabel(false);
            setNewLabelName("");
            setNewLabelColor(PRESET_COLORS[0]);
        } catch (err) {
            console.error("Failed to create label:", err);
        } finally {
            setSavingLabel(false);
        }
    };

    // Save changes
    const handleSave = async () => {
        if (!canSave) return;
        setSaving(true);
        setError(null);

        try {
            const payload = {
                name: productName.trim(),
                default_quantity: defaultQuantity,
                label_ids: selectedLabelIds,
            };

            if (!settingsLocked && settingsId) {
                payload.settings_id = settingsId;
            }

            const response = await axios.put(`${baseUrl}/products/${product.id}`, payload);
            const updated = response.data;

            // Re-fetch price if settings or quantity changed
            const settingsChanged = !isCustom && settingsId && settingsId !== product.settings_id;
            const quantityChanged = defaultQuantity !== product.default_quantity;

            if (settingsChanged || quantityChanged) {
                fetchLibraryProductPrice(updated).then((withPrice) => {
                    onProductUpdated(withPrice);
                });
            } else {
                onProductUpdated(updated);
            }
        } catch (err) {
            console.error("Failed to update product:", err);
            setError(err.response?.data?.message || "Something went wrong while saving.");
            setSaving(false);
        }
    };

    // Delete product
    const handleDelete = async () => {
        try {
            await axios.delete(`${baseUrl}/products/${product.id}`);
            onProductDeleted(product.id);
        } catch (err) {
            console.error("Failed to delete product:", err);
            setError("Failed to delete.");
        }
    };

    // Build modelsData to pre-fill ProductSettings with current product settings
    const modelsData = useMemo(() => {
        if (!product.settings) return [{ data: [] }];
        return [
            {
                data: [
                    {
                        checked: true,
                        settings: product.settings,
                    },
                ],
            },
        ];
    }, [product.settings]);

    return (
        <>
            <div className="library-slideover-overlay" onClick={onClose} />
            <div className="library-slideover">
                <div className="library-slideover-header">
                    <h3>Edit product</h3>
                    <button className="library-slideover-close" onClick={onClose}>
                        <FontAwesomeIcon icon={faXmark} size="lg" />
                    </button>
                </div>

                <div className="library-slideover-body">
                    {/* Interactive 3D viewer */}
                    {product.model_url && (
                        <div className="library-model-preview">
                            <DetailModelViewer
                                modelUrl={product.model_url}
                                extension={product.extension}
                                containerId={`library-detail-preview-${product.id}`}
                                dimensions={product.dimensions}
                            />
                        </div>
                    )}
                    {/* Fallback to static image if no model URL */}
                    {!product.model_url && product.preview_image && (
                        <div className="library-detail-preview">
                            <img src={product.preview_image} alt={product.name} />
                        </div>
                    )}

                    {/* File name */}
                    {product.file && (
                        <div className="library-file-info" style={{ marginBottom: 16 }}>
                            <span>{product.file}</span>
                        </div>
                    )}

                    {/* Product name + labels + settings + quantity */}
                    <div>
                        {/* Product name */}
                        <div className="library-form-group">
                            <label>Product name</label>
                            <input
                                type="text"
                                value={productName}
                                onChange={(e) => setProductName(e.target.value)}
                                placeholder="Give your product a name"
                                className="library-input"
                            />
                        </div>

                        {/* Labels */}
                        <div className="library-form-group">
                            <label>Labels</label>
                            <div className="library-label-select">
                                {labels.map((label) => (
                                    <button
                                        key={label.id}
                                        type="button"
                                        className={`library-label-chip-select ${
                                            selectedLabelIds.includes(label.id) ? "active" : ""
                                        }`}
                                        style={label.color ? { "--label-color": label.color } : {}}
                                        onClick={() => {
                                            setSelectedLabelIds((prev) =>
                                                prev.includes(label.id)
                                                    ? prev.filter((id) => id !== label.id)
                                                    : [...prev, label.id]
                                            );
                                        }}
                                    >
                                        {label.color && (
                                            <span
                                                className="library-label-dot"
                                                style={{ backgroundColor: label.color }}
                                            />
                                        )}
                                        {label.name}
                                    </button>
                                ))}
                                {!showNewLabel && (
                                    <button
                                        type="button"
                                        className="library-label-add-btn"
                                        onClick={() => setShowNewLabel(true)}
                                    >
                                        <FontAwesomeIcon icon={faPlus} /> New
                                    </button>
                                )}
                            </div>

                            {showNewLabel && (
                                <div className="library-label-inline-form">
                                    <input
                                        type="text"
                                        className="library-input"
                                        placeholder="Label name"
                                        value={newLabelName}
                                        onChange={(e) => setNewLabelName(e.target.value)}
                                        onKeyDown={(e) => e.key === "Enter" && handleCreateLabel()}
                                        autoFocus
                                    />
                                    <div className="library-color-picker">
                                        {PRESET_COLORS.map((c) => (
                                            <button
                                                key={c}
                                                type="button"
                                                className={`library-color-swatch ${
                                                    newLabelColor === c ? "active" : ""
                                                }`}
                                                style={{ backgroundColor: c }}
                                                onClick={() => setNewLabelColor(c)}
                                            />
                                        ))}
                                    </div>
                                    <div className="library-label-form-actions">
                                        <button
                                            type="button"
                                            className="library-btn-cancel"
                                            onClick={() => {
                                                setShowNewLabel(false);
                                                setNewLabelName("");
                                                setNewLabelColor(PRESET_COLORS[0]);
                                            }}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            className="library-btn-save"
                                            onClick={handleCreateLabel}
                                            disabled={!newLabelName.trim() || savingLabel}
                                        >
                                            {savingLabel ? "Saving..." : "Add"}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* No price available notice */}
                        {(product.price === null || product.price === undefined) && (
                            <div className="library-on-request-notice">
                                <span className="library-on-request-badge">Not available</span>
                                <p>
                                    No price is available for this product configuration. It cannot be added to cart or
                                    ordered.
                                </p>
                            </div>
                        )}

                        {/* Settings */}
                        <div className="library-form-group">
                            <label>Settings</label>
                            {isCustom && (
                                <div className="library-settings-locked-notice">
                                    Settings are fixed for this product. Our team used this configuration to determine
                                    the price.
                                </div>
                            )}
                            {!isCustom && hasFixedPrice && (
                                <div className="library-settings-locked-notice">
                                    Settings are locked because this product has a fixed price.
                                </div>
                            )}
                        </div>
                        <div className={`library-product-settings ${settingsLocked ? "library-settings-locked" : ""}`}>
                            <ProductSettings
                                modelsData={modelsData}
                                textData={textData}
                                updateModelSettings={() => {}}
                                saveProductSettings={() => {}}
                                updateSelectedModelsColor={() => {}}
                                updateModelDeliveryTime={() => {}}
                                updateModelOrientation={() => {}}
                                updatePricesOnNextIteration={() => {}}
                                setUpdatingMultipleModels={() => {}}
                                setSaveFooter={() => {}}
                                saveFooter={false}
                                disableProductSettings={settingsLocked}
                                loggedInUser={loggedInUser}
                                labelLogo={labelLogo}
                                setChosenConfig={setChosenConfig}
                                productSettings={productSettings}
                                isLibrary={true}
                            />
                        </div>

                        {/* Default quantity */}
                        <div className="library-form-group">
                            <label>Default quantity</label>
                            <input
                                type="number"
                                min="1"
                                value={defaultQuantity}
                                onChange={(e) => setDefaultQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                                className="library-input library-input-quantity"
                                readOnly={hasFixedPrice}
                                disabled={hasFixedPrice}
                                title={
                                    hasFixedPrice
                                        ? "Quantity is locked because the binnendienst set a fixed price."
                                        : undefined
                                }
                            />
                            {hasFixedPrice && (
                                <p className="library-form-help">
                                    Quantity is locked because this product has a fixed price agreement.
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Error */}
                    {error && <div className="library-error">{error}</div>}

                    <div className="library-detail-delete-section">
                        {!confirmDelete ? (
                            <button
                                className="library-btn-delete-icon"
                                onClick={() => setConfirmDelete(true)}
                                title="Delete product"
                            >
                                <FontAwesomeIcon icon={faTrash} /> Delete
                            </button>
                        ) : (
                            <div className="library-detail-confirm-delete">
                                <p>Are you sure you want to delete this product?</p>
                                <div className="library-detail-confirm-actions">
                                    <button className="library-btn-cancel" onClick={() => setConfirmDelete(false)}>
                                        No
                                    </button>
                                    <button className="library-btn-delete" onClick={handleDelete}>
                                        Yes, delete
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="library-slideover-footer">
                    <div className="library-slideover-footer-right">
                        {product.price !== null && product.price !== undefined && (
                            <button
                                className="library-btn-add-cart"
                                onClick={() => addProductToCart(product, navigateToCheckout)}
                            >
                                <FontAwesomeIcon icon={faCartPlus} /> Add to cart
                            </button>
                        )}
                        <button
                            className="library-btn-request-quotation"
                            onClick={() => requestSingleProductQuotation(product, navigateToQuotations)}
                        >
                            <FontAwesomeIcon icon={faFileLines} /> Request quotation
                        </button>
                        <button className="library-btn-save" onClick={handleSave} disabled={!canSave}>
                            {saving ? "Saving..." : "Save"}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
}
